// ⚖️ MentionsLegales - Page des mentions légales du site
// Informations légales obligatoires (SIREN, SIRET, hébergeur, etc.)
// Utilisé dans : src/App.jsx (route "/legal")

import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import legalData from '@/locales/legal.json'

/**
 * Page des mentions légales complètes
 * Contient toutes les informations légales obligatoires pour un site d'association
 * Sections : Éditeur, Directeur de publication, Hébergement, Propriété intellectuelle, RGPD
 */
export default function MentionsLegales() {
  const { mentionsLegales, association, hosting, contact, navigation } = legalData

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 transition-colors">
      <div className="container-custom px-4 py-24 max-w-4xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 mb-8 transition"
        >
          <ArrowLeft size={20} />
          {navigation.backToHome}
        </Link>

        <h1 className="text-4xl font-display font-bold text-primary-600 dark:text-primary-400 mb-8">
          {mentionsLegales.title}
        </h1>

        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-8 shadow-lg space-y-8 text-neutral-700 dark:text-neutral-300">
          
          {/* Éditeur du site */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {mentionsLegales.sections.editor.title}
            </h2>
            <p className="space-y-1">
              <strong>{association.name}</strong><br />
              {association.legalForm}<br />
              {association.address.street}<br />
              {association.address.postalCode} {association.address.city}
            </p>
            <p className="mt-4 space-y-1">
              <strong>{mentionsLegales.sections.editor.labels.siren}</strong> {association.siren}<br />
              <strong>{mentionsLegales.sections.editor.labels.siret}</strong> {association.siret}<br />
              <strong>{mentionsLegales.sections.editor.labels.tva}</strong> {association.tva}<br />
              <strong>{mentionsLegales.sections.editor.labels.creationDate}</strong> {association.creationDate}<br />
              <strong>{mentionsLegales.sections.editor.labels.activity}</strong> {association.activity} ({association.codeNAF})
            </p>
            <p className="mt-4">
              <strong>{mentionsLegales.sections.editor.labels.contact}</strong><br />
              {mentionsLegales.sections.editor.labels.email} {contact.email}
            </p>
          </section>

          {/* Directeur de publication */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {mentionsLegales.sections.director.title}
            </h2>
            <p>{association.director}</p>
            <p className="text-sm mt-2 text-neutral-600 dark:text-neutral-400">{mentionsLegales.sections.director.responsibleLabel} {association.legalResponsible}</p>
          </section>

          {/* Hébergement */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {mentionsLegales.sections.hosting.title}
            </h2>
            <p className="mb-2">
              {mentionsLegales.sections.hosting.intro}<br />
              <strong>{hosting.name}</strong><br />
              {hosting.address}
            </p>
          </section>

          {/* Conception et développement */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {mentionsLegales.sections.development.title}
            </h2>
            <div>
              <p className="font-semibold text-neutral-900 dark:text-neutral-100">{mentionsLegales.sections.development.domelvia.name}</p>
              <p className="text-sm mb-1 italic text-neutral-600 dark:text-neutral-400">{mentionsLegales.sections.development.domelvia.tagline}</p>
              <p className="text-sm">
                {mentionsLegales.sections.development.domelvia.legalName}<br />
                {mentionsLegales.sections.development.domelvia.legalForm}<br />
                SIREN : {mentionsLegales.sections.development.domelvia.siren}<br />
                {mentionsLegales.sections.development.domelvia.rcs}<br />
                {mentionsLegales.sections.development.domelvia.address.street}, {mentionsLegales.sections.development.domelvia.address.postalCode} {mentionsLegales.sections.development.domelvia.address.city}
              </p>
              <p className="text-sm mt-3">
                <strong>Contact :</strong><br />
                Email : <a href={`mailto:${mentionsLegales.sections.development.domelvia.contact.email}`} className="text-primary-600 dark:text-primary-400 hover:underline">{mentionsLegales.sections.development.domelvia.contact.email}</a>
              </p>
              <a 
                href="https://www.societe.com/societe/superadmin-988253787.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:underline text-sm inline-block mt-2"
              >
                Voir la fiche société →
              </a>
            </div>
          </section>

          {/* Propriété intellectuelle */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {mentionsLegales.sections.intellectualProperty.title}
            </h2>
            <p className="mb-4">
              {mentionsLegales.sections.intellectualProperty.content.replace('{associationName}', association.name)}
            </p>
            <p className="mb-4">
              {mentionsLegales.sections.intellectualProperty.rights}
            </p>
            <p>
              {mentionsLegales.sections.intellectualProperty.trademarks}
            </p>
          </section>

          {/* Protection des données */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {mentionsLegales.sections.dataProtection.title}
            </h2>
            <p>
              {mentionsLegales.sections.dataProtection.intro}
            </p>
            <p className="mt-4">
              {mentionsLegales.sections.dataProtection.exerciseRights.replace('{email}', contact.email)}
            </p>
            <p className="mt-4">
              {mentionsLegales.sections.dataProtection.moreInfo} <Link to="/privacy-policy" className="text-primary-600 dark:text-primary-400 hover:underline">{mentionsLegales.sections.dataProtection.privacyPolicyLink}</Link>.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {mentionsLegales.sections.cookies.title}
            </h2>
            <p className="mb-4">
              {mentionsLegales.sections.cookies.intro}
            </p>
            <p>
              {mentionsLegales.sections.cookies.noCookies}
            </p>
          </section>

        </div>
      </div>
    </div>
  )
}
